// Registration questions (workshop_field / workshop_field_option, server
// V095). The contract is zebra-portal-server docs/events-custom-fields.md:
// GET/PUT /api/workshops/fields/:workshopId send and receive the whole
// definition, and the portal-only helpers here build and check that body.
// Form sections (workshop_field_section, V102; docs/events-types-sessions-
// contract.md section D) are headings that group questions: questions with no
// section come first, then each section in order, its questions in order.
// Priced options (V103, section E): an answer choice may carry a price_adj,
// added to the participant's fee when chosen. Max choices: a multiple choice
// may cap how many answers a participant ticks (max_choices, null = no limit).

// The seven types the server accepts, in the order the type picker shows them.
export const FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "Single choice (radio or dropdown)" },
  { value: "multi_select", label: "Multiple choice (tick all that apply)" },
  { value: "checkbox", label: "Acknowledgement (one tick box)" },
];

export const typeHasOptions = (type) =>
  type === "select" || type === "multi_select" || type === "checkbox";

export const isActive = (row) => Number(row.active_id) === 1;

// The types whose options may carry a price (all three option types).
export const typeHasPrices = typeHasOptions;

// Money in cents, so sums of prices don't pick up float noise.
export const roundMoney = (n) => Math.round((Number(n) || 0) * 100) / 100;

// "50", "12.50", "-49": no trailing ".00" on whole amounts.
export const formatMoney = (n) => {
  const v = roundMoney(n);
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
};

// What the price box holds: "" for 0, else the amount as typed / stored.
export const priceToEditor = (priceAdj) =>
  roundMoney(priceAdj) === 0 ? "" : formatMoney(priceAdj);

// A price box's text: blank (0), or dollars and cents under 100000 (the
// server's cap), negative allowed, "5", "5.5", ".5" and "5." alike.
export const isValidPriceText = (text) =>
  String(text ?? "").trim() === "" ||
  /^-?(\d{1,5}(\.\d{0,2})?|\.\d{1,2})$/.test(String(text).trim());

// The price box's text -> price_adj for the PUT (blank = 0).
export const priceFromEditor = (text) =>
  isValidPriceText(text) ? roundMoney(String(text ?? "").trim() || 0) : 0;

export const byOrderNum = (a, b) => (Number(a.order_num) || 0) - (Number(b.order_num) || 0);

// New options and sections get a temporary negative id so a dependency (or
// a question's section) can point at one saved in the same PUT; the server
// inserts and maps them.
let lastTempId = 0;
export const nextTempOptionId = () => --lastTempId;
export const nextTempSectionId = nextTempOptionId;

// What the "Max choices" box holds: "" for no limit, else the number.
export const maxChoicesToEditor = (maxChoices) =>
  maxChoices == null ? "" : String(maxChoices);

// The box's text -> max_choices for the PUT: null when blank or not for a
// multiple choice (the server takes a limit on that type only).
export const maxChoicesFromEditor = (field) =>
  field.field_type === "multi_select" && String(field.max_choices ?? "").trim() !== ""
    ? Number(String(field.max_choices).trim())
    : null;

// Whether a GET's fields come from a server that knows max_choices: one that
// does sends the key on every field, so only an event with no questions
// leaves it open (a server without it ignores the key, as with price_adj).
export const maxChoicesSupported = (fields) =>
  !fields?.length || fields.some((f) => "max_choices" in f);

// The server's 400 checks on a multiple choice's limit: a whole number of 1
// or more, and not more than its active answers. null when fine or blank.
export function maxChoicesProblem(field) {
  const text = String(field.max_choices ?? "").trim();
  if (field.field_type !== "multi_select" || text === "") return null;
  if (!/^\d+$/.test(text) || Number(text) < 1) return "max choices must be a whole number, 1 or more";
  const active = field.options.filter(isActive).length;
  if (Number(text) > active)
    return `max choices can't be more than its ${active} active answer${active === 1 ? "" : "s"}`;
  return null;
}

// GET results -> editor sections, in order.
export const sectionsToEditor = (sections) =>
  [...(sections || [])].sort(byOrderNum).map((s) => ({
    workshop_field_section_id: s.workshop_field_section_id,
    title: s.title || "",
    description: s.description || "",
    active_id: isActive(s) ? 1 : 0,
  }));

export const newEditorSection = () => ({
  workshop_field_section_id: nextTempSectionId(),
  title: "",
  description: "",
  active_id: 1,
});

// The sections in the order the builder shows them: hidden ones first, then
// the shown ones, each keeping its own order. The form asks a hidden
// section's questions with the ungrouped ones, by order_num, so ordering the
// questions by this (orderBySection) puts them right after the ungrouped
// questions, where they are asked. The sections' own order_num still comes
// from `sections`, so a section shown again goes back to its place.
export const formSectionOrder = (sections) => [
  ...(sections || []).filter((s) => !isActive(s)),
  ...(sections || []).filter(isActive),
];

// Questions in the order the form asks them: those with no section (or one
// not in `sections`) first, then each section's in the sections' order,
// keeping their relative order. `sections` is already ordered.
export function orderBySection(fields, sections) {
  const rank = new Map((sections || []).map((s, i) => [s.workshop_field_section_id, i + 1]));
  const sectionRank = (f) => rank.get(f.workshop_field_section_id) || 0;
  return fields
    .map((f, i) => ({ f, i }))
    .sort((a, b) => sectionRank(a.f) - sectionRank(b.f) || a.i - b.i)
    .map(({ f }) => f);
}

// [{ section: null | section, fields: [{ field, index }] }]: the no-section
// group, then one per section (empty ones included), where index is the
// field's position in `fields`. `fields` should be in orderBySection order.
export function groupBySection(fields, sections) {
  const known = new Set((sections || []).map((s) => s.workshop_field_section_id));
  const entries = fields.map((field, index) => ({ field, index }));
  return [
    {
      section: null,
      fields: entries.filter(({ field }) => !known.has(field.workshop_field_section_id)),
    },
    ...(sections || []).map((section) => ({
      section,
      fields: entries.filter(
        ({ field }) => field.workshop_field_section_id === section.workshop_field_section_id
      ),
    })),
  ];
}

let lastUid = 0;
const nextUid = () => `f${++lastUid}`;

// GET results -> editor rows: in form order (sections: sectionsToEditor of
// the same results), numbers as numbers, plus a client-only _uid for React
// keys (a new field has no workshop_field_id yet).
export function fieldsToEditor(fields, sections) {
  const rows = [...(fields || [])].sort(byOrderNum).map((f) => ({
    _uid: nextUid(),
    workshop_field_id: f.workshop_field_id,
    workshop_field_section_id: f.workshop_field_section_id ?? null,
    field_key: f.field_key || "",
    label: f.label || "",
    help_text: f.help_text || "",
    field_type: f.field_type,
    required_ind: Number(f.required_ind) === 1 ? 1 : 0,
    active_id: isActive(f) ? 1 : 0,
    depends_on_option_id: f.depends_on_option_id ?? null,
    max_choices: maxChoicesToEditor(f.max_choices),
    options: [...(f.options || [])].sort(byOrderNum).map((o) => ({
      workshop_field_option_id: o.workshop_field_option_id,
      label: o.label || "",
      active_id: isActive(o) ? 1 : 0,
      price_adj: priceToEditor(o.price_adj),
    })),
  }));
  return orderBySection(rows, sections);
}

export const newEditorField = () => ({
  _uid: nextUid(),
  workshop_field_section_id: null,
  field_key: "",
  label: "",
  help_text: "",
  field_type: "text",
  required_ind: 0,
  active_id: 1,
  depends_on_option_id: null,
  max_choices: "",
  options: [],
});

export const newEditorOption = (label = "", priceAdj = "") => ({
  workshop_field_option_id: nextTempOptionId(),
  label,
  active_id: 1,
  price_adj: priceAdj,
});

// Editor rows -> the PUT body. order_num is the row's position (1..n; fields
// are kept in orderBySection order, so it also orders them within their
// section), a new field has no workshop_field_id and a blank field_key (the
// server makes one from the label), and a type without options sends none.
// Every option carries its price_adj (a blank box is 0; a server without
// V103 ignores the key).
// sections: the editor's sections, or undefined for a server without them
// (V102), which then gets the body it always did. withMaxChoices: the server
// knows max_choices (maxChoicesSupported); every field then carries it.
export function buildFieldsPutBody(fields, sections, { withMaxChoices = false } = {}) {
  return {
    ...(sections
      ? {
          sections: sections.map((s, i) => ({
            workshop_field_section_id: s.workshop_field_section_id,
            title: s.title.trim(),
            description: s.description?.trim() || null,
            order_num: i + 1,
            active_id: s.active_id ? 1 : 0,
          })),
        }
      : {}),
    fields: fields.map((f, i) => ({
      ...(f.workshop_field_id ? { workshop_field_id: f.workshop_field_id } : {}),
      ...(sections ? { workshop_field_section_id: f.workshop_field_section_id ?? null } : {}),
      field_key: f.field_key || "",
      label: f.label.trim(),
      help_text: f.help_text?.trim() || null,
      field_type: f.field_type,
      required_ind: f.required_ind ? 1 : 0,
      order_num: i + 1,
      active_id: f.active_id ? 1 : 0,
      depends_on_option_id: f.depends_on_option_id ?? null,
      ...(withMaxChoices ? { max_choices: maxChoicesFromEditor(f) } : {}),
      options: typeHasOptions(f.field_type)
        ? f.options.map((o, j) => ({
            workshop_field_option_id: o.workshop_field_option_id,
            label: o.label.trim(),
            order_num: j + 1,
            active_id: o.active_id ? 1 : 0,
            price_adj: priceFromEditor(o.price_adj),
          }))
        : [],
    })),
  };
}

// "Copy questions from another event": the source's active fields and active
// options as new editor rows (no field ids; options re-keyed to temp ids so
// the copied dependencies point at the copied options). A field whose
// dependency did not survive the copy (inactive option or field) is left
// out, since on the source it could never be shown either. A field_key the
// target already uses is blanked so the server makes a new one; otherwise it
// is kept, which is what lets a later change match "cfc_id" across events.
// existingKeys must include keys of questions the editor has removed but the
// server still holds (it deactivates rather than deletes a question with
// answers, so its key stays taken). forceOptional: copying onto a locked
// event, where a new question may only be optional. sectionIdMap: the
// source's section id -> its copy's (copySectionsFromSource); a question
// whose section was not copied comes over with no section. copyPrices: the
// target's type allows option prices, so the copies keep the source's.
export function copyFieldsFromSource(
  sourceFields,
  existingKeys = [],
  { forceOptional = false, sectionIdMap = new Map(), copyPrices = false } = {}
) {
  const taken = new Set(existingKeys.filter(Boolean));
  const optionIdMap = new Map();
  const candidates = [...(sourceFields || [])]
    .filter(isActive)
    .sort(byOrderNum)
    .map((f) => ({
      ...newEditorField(),
      workshop_field_section_id: sectionIdMap.get(f.workshop_field_section_id) ?? null,
      field_key: f.field_key && !taken.has(f.field_key) ? f.field_key : "",
      label: f.label || "",
      help_text: f.help_text || "",
      field_type: f.field_type,
      required_ind: !forceOptional && Number(f.required_ind) === 1 ? 1 : 0,
      depends_on_option_id: f.depends_on_option_id ?? null,
      // Only active answers come over, so a limit the source could save
      // still fits.
      max_choices:
        f.field_type === "multi_select" ? maxChoicesToEditor(f.max_choices) : "",
      options: typeHasOptions(f.field_type)
        ? [...(f.options || [])]
            .filter(isActive)
            .sort(byOrderNum)
            .map((o) => {
              const copy = newEditorOption(
                o.label || "",
                copyPrices ? priceToEditor(o.price_adj) : ""
              );
              optionIdMap.set(o.workshop_field_option_id, copy.workshop_field_option_id);
              return copy;
            })
        : [],
    }));

  // Drop fields whose dependency points nowhere, repeatedly, since dropping a
  // field also drops the options its dependants pointed at.
  let kept = candidates;
  for (;;) {
    const liveOptions = new Set(
      kept.flatMap((f) => f.options.map((o) => o.workshop_field_option_id))
    );
    const next = kept.filter(
      (f) =>
        f.depends_on_option_id == null ||
        liveOptions.has(optionIdMap.get(f.depends_on_option_id))
    );
    if (next.length === kept.length) break;
    kept = next;
  }
  return kept.map((f) => ({
    ...f,
    depends_on_option_id:
      f.depends_on_option_id == null ? null : optionIdMap.get(f.depends_on_option_id),
  }));
}

// The source's active sections as new editor sections, in order, and the map
// from each source section id to its copy's temporary id.
export function copySectionsFromSource(sourceSections) {
  const sectionIdMap = new Map();
  const sections = [...(sourceSections || [])]
    .filter(isActive)
    .sort(byOrderNum)
    .map((s) => {
      const copy = {
        ...newEditorSection(),
        title: s.title || "",
        description: s.description || "",
      };
      sectionIdMap.set(s.workshop_field_section_id, copy.workshop_field_section_id);
      return copy;
    });
  return { sections, sectionIdMap };
}

// Option-type fields above `index` and their active options: what the "Show
// only when … is …" picker offers for a new choice. Only earlier questions,
// so the website (which asks them in order) has the answer first. A stored
// dependency outside these (its answer since deactivated, or the question
// moved) is still valid; see describeDependency and validateFieldsDefinition.
export function dependencyChoices(fields, index) {
  return fields
    .slice(0, index)
    .filter((f) => isActive(f) && typeHasOptions(f.field_type))
    .map((f) => ({
      field: f,
      options: f.options.filter(
        (o) => isActive(o) && o.label.trim() && o.workshop_field_option_id != null
      ),
    }))
    .filter((c) => c.options.length > 0);
}

// Option id -> the question that owns it, for the options the PUT body will
// carry (a type without options sends none).
const optionOwners = (fields) => {
  const owners = new Map();
  fields.forEach((f) => {
    if (typeHasOptions(f.field_type))
      f.options.forEach((o) => owners.set(o.workshop_field_option_id, f));
  });
  return owners;
};

// '"Section" is U10', with "(inactive)" when that answer or question is
// hidden from new registrations, for a dependency the picker no longer offers.
export function describeDependency(fields, optionId) {
  const owner = optionOwners(fields).get(optionId);
  if (!owner) return "(an answer that is no longer on this event)";
  const option = owner.options.find((o) => o.workshop_field_option_id === optionId);
  const inactive = !isActive(owner) || !isActive(option);
  return `only when "${owner.label.trim() || "(untitled)"}" is ${option.label}${inactive ? " (inactive)" : ""}`;
}

const fieldName = (f, i) => f.label.trim() || `Question ${i + 1}`;

export const sectionName = (s, i) => s.title.trim() || `Section ${i + 1}`;

// The server's 400 checks on sections (title required, lengths).
export function validateSectionsDefinition(sections) {
  const errors = [];
  (sections || []).forEach((s, i) => {
    if (!s.title.trim()) errors.push(`Section ${i + 1}: enter the section title`);
    if (s.title.trim().length > 200) errors.push(`${sectionName(s, i)}: the title is over 200 characters`);
    if ((s.description || "").trim().length > 2000)
      errors.push(`${sectionName(s, i)}: the description is over 2000 characters`);
  });
  return errors;
}

// The server's 400 checks, made before the PUT so staff see every problem at
// once (one message each). Kept to exactly what the server enforces: option
// counts only for active questions and only counting active answers, and a
// dependency that points at an answer in this definition, not the question's
// own and not in a loop. An inactive or lower parent is allowed.
// prices: { allowed, typeName, fee } for the option-price rules (V103):
// non-zero prices only where the event's type allows them, and no way for
// the cheapest answers of all the questions together to take the event's
// fee below 0 (priceFloorBreach).
export function validateFieldsDefinition(fields, prices = { allowed: true, fee: null }) {
  const errors = [];
  const owners = optionOwners(fields);
  fields.forEach((f, i) => {
    const name = fieldName(f, i);
    if (!f.label.trim()) errors.push(`Question ${i + 1}: enter the question`);
    if (f.label.trim().length > 200) errors.push(`${name}: the question is over 200 characters`);
    if ((f.help_text || "").trim().length > 1000)
      errors.push(`${name}: the help text is over 1000 characters`);
    if (!FIELD_TYPES.some((t) => t.value === f.field_type))
      errors.push(`${name}: choose an answer type`);

    if (typeHasOptions(f.field_type)) {
      f.options.forEach((o, j) => {
        if (!o.label.trim()) errors.push(`${name}: answer ${j + 1} is blank`);
        if (o.label.trim().length > 200)
          errors.push(`${name}: answer ${j + 1} is over 200 characters`);
      });
      errors.push(...priceProblems(f, name, prices));
      const limitProblem = maxChoicesProblem(f);
      if (limitProblem) errors.push(`${name}: ${limitProblem}`);
      const active = f.options.filter(isActive);
      if (isActive(f)) {
        if (f.field_type === "checkbox" && active.length !== 1)
          errors.push(`${name}: a tick box needs exactly one active answer (the text beside the box)`);
        else if (active.length === 0) errors.push(`${name}: add at least one answer`);
      }
    }

    if (f.depends_on_option_id != null) {
      const owner = owners.get(f.depends_on_option_id);
      if (!owner)
        errors.push(`${name}: "Show only when" points at an answer that is no longer on this event`);
      else if (owner === f)
        errors.push(`${name}: "Show only when" cannot point at one of its own answers`);
    }
  });

  // No loops (A shown on B's answer, B shown on A's).
  fields.forEach((f, i) => {
    let cur = f;
    for (let steps = 0; cur && cur.depends_on_option_id != null; steps++) {
      if (steps > fields.length) {
        errors.push(`${fieldName(f, i)}: its "Show only when" chain loops back on itself`);
        break;
      }
      const next = owners.get(cur.depends_on_option_id);
      // Pointing at its own answer is reported above, not as a loop.
      cur = next === cur ? null : next;
    }
  });

  if (prices.allowed !== false && prices.fee != null) {
    const tipped = priceFloorBreach(fields, prices.fee);
    if (tipped)
      errors.push(
        `${fieldName(tipped, fields.indexOf(tipped))}: with the other questions' prices this could take the participant fee below $0 (event fee $${roundMoney(
          prices.fee
        ).toFixed(2)})`
      );
  }
  return errors;
}

// The server's $0 floor (helpers/workshopFields.js priceFloorBreach): the
// lowest fee the prices allow is the event fee plus, for each active option
// question in form order, the least its active answers can add. A question
// everyone must answer (required, no "Show only when") adds its cheapest
// answer, which may be positive (a multiple choice: its negative answers,
// or its cheapest when none is negative); any other adds at most 0 (a
// single choice its cheapest capped at 0, a multiple choice its negative
// answers, a tick box its price when negative). Returns the question at
// which that running total first drops below 0, else null.
export function priceFloorBreach(fields, fee) {
  let cents = Math.round((Number(fee) || 0) * 100);
  for (const f of fields) {
    if (!isActive(f) || !typeHasOptions(f.field_type)) continue;
    const prices = f.options
      .filter(isActive)
      .map((o) => Math.round(priceFromEditor(o.price_adj) * 100));
    if (!prices.length) continue;
    const mustAnswer = Number(f.required_ind) === 1 && f.depends_on_option_id == null;
    const negatives = prices.filter((p) => p < 0).reduce((a, b) => a + b, 0);
    if (f.field_type === "multi_select") {
      cents += negatives < 0 || !mustAnswer ? negatives : Math.min(...prices);
    } else {
      cents += mustAnswer ? Math.min(...prices) : Math.min(0, ...prices);
    }
    if (cents < 0) return f;
  }
  return null;
}

// The server's 400 wording for prices, so the client's refusal reads the same.
export const pricesNotAllowedMessage = (typeName) =>
  `Option prices are not allowed on ${typeName || "this type of"} events`;

// One question's price problems (an option type; see validateFieldsDefinition,
// which checks the $0 floor across questions).
function priceProblems(f, name, { allowed = true, typeName } = {}) {
  const errors = [];
  f.options.forEach((o, j) => {
    if (!isValidPriceText(o.price_adj))
      errors.push(
        `${name}: "${o.label.trim() || `Answer ${j + 1}`}" price must be a number of dollars and cents`
      );
  });
  const priced = f.options.filter((o) => priceFromEditor(o.price_adj) !== 0);
  if (priced.length === 0) return errors;
  if (!allowed) errors.push(`${name}: ${pricesNotAllowedMessage(typeName).replace(/^O/, "o")}`);
  return errors;
}

// What the server will refuse with 409 once registrations exist (the lock
// rule), worked out per field from the definition as last loaded / saved.
// `stored` is the GET/PUT result's fields.
export function lockState(stored, locked) {
  const storedFields = new Map(
    (stored || []).filter((f) => f.workshop_field_id).map((f) => [f.workshop_field_id, f])
  );
  const storedOptionIds = new Set(
    (stored || []).flatMap((f) => (f.options || []).map((o) => o.workshop_field_option_id))
  );
  return {
    locked: !!locked,
    // Delete (vs deactivate) a field or option, change a stored field's type
    // or dependency: only while unlocked.
    canDeleteField: (f) => !locked || !storedFields.has(f.workshop_field_id),
    canDeleteOption: (o) => !locked || !storedOptionIds.has(o.workshop_field_option_id),
    canChangeType: (f) => !locked || !storedFields.has(f.workshop_field_id),
    canChangeDependency: (f) => !locked || !storedFields.has(f.workshop_field_id),
    // price_adj is frozen on a saved answer; a new one may still be priced.
    canChangePrice: (o) => !locked || !storedOptionIds.has(o.workshop_field_option_id),
    // Optional -> required is refused; a new field may only be added
    // optional, and so may a stored inactive one brought back (the server
    // treats reactivating a required question as adding one).
    canMakeRequired: (f) => {
      if (!locked) return true;
      const s = storedFields.get(f.workshop_field_id);
      if (!s || (!isActive(s) && isActive(f))) return false;
      return Number(s.required_ind) === 1;
    },
    // A saved question's max choices can only be raised or cleared once
    // locked (no limit counts as the highest); blank or not a number is left
    // to maxChoicesProblem.
    canSetMaxChoices: (f) => {
      const s = storedFields.get(f.workshop_field_id);
      const next = maxChoicesFromEditor(f);
      if (!locked || !s || next == null || !Number.isFinite(next)) return true;
      return s.max_choices != null && next >= Number(s.max_choices);
    },
    // Reactivating a question stored as inactive: optional only, once locked.
    reactivatesAsOptional: (f) => {
      const s = storedFields.get(f.workshop_field_id);
      return !!locked && !!s && !isActive(s);
    },
  };
}
